import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function SeoArticlesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [selected, setSelected] = useState(null); // article being viewed/edited
  const [showHistory, setShowHistory] = useState(false);
  const aiQuota = useAiQuota();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/seo-articles');
      setData(r.data);
    } catch (e) {
      setMsg(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateNow = async () => {
    if (!aiQuota.eligible) { setMsg('AI article generation is a TurboCharge-only feature.'); return; }
    if (aiQuota.remaining <= 0) { setMsg(`Monthly AI pull quota reached (${aiQuota.used}/${aiQuota.limit}). Resets on the 1st of next month.`); return; }
    setGenerating(true); setMsg('');
    try {
      const r = await api.post('/dashboard/seo-articles/generate');
      setMsg(`Generated ${r.data.generated} new article${r.data.generated === 1 ? '' : 's'}. Review them below.`);
      await load();
      aiQuota.refresh();
    } catch (e) {
      setMsg(formatApiError(e));
    } finally { setGenerating(false); }
  };

  const openArticle = async (id) => {
    try {
      const r = await api.get(`/dashboard/seo-articles/${id}`);
      setSelected(r.data);
      setShowHistory(false);
    } catch (e) { setMsg(formatApiError(e)); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/dashboard/seo-articles/${id}/approve`);
      setMsg('Article published to your website.');
      await load();
      if (selected?.id === id) {
        const r = await api.get(`/dashboard/seo-articles/${id}`);
        setSelected(r.data);
      }
    } catch (e) { setMsg(formatApiError(e)); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this article? It will not be published.')) return;
    try {
      await api.post(`/dashboard/seo-articles/${id}/reject`);
      setMsg('Article rejected.');
      await load();
      setSelected(null);
    } catch (e) { setMsg(formatApiError(e)); }
  };

  const unpublish = async (id) => {
    if (!window.confirm('Unpublish this article from your public website?')) return;
    try {
      await api.post(`/dashboard/seo-articles/${id}/unpublish`);
      setMsg('Article unpublished.');
      await load();
      const r = await api.get(`/dashboard/seo-articles/${id}`);
      setSelected(r.data);
    } catch (e) { setMsg(formatApiError(e)); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400 animate-pulse">Loading articles...</div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Unable to load.</div>;

  const used = data.this_month;
  const quota = data.monthly_quota;
  const remaining = Math.max(0, quota - used);

  return (
    <div className="space-y-6" data-testid="seo-articles-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI-Generated SEO Articles
          </h2>
          <p className="text-sm text-slate-500">
            Tap <b>Generate</b> to produce up to <b>{quota}</b> SEO articles this month. Review, edit if needed, then publish — counts against your monthly 2 AI pulls.
          </p>
        </div>
        <button
          onClick={generateNow}
          disabled={generating || remaining === 0 || !aiQuota.eligible || aiQuota.remaining <= 0}
          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-xl text-sm font-medium hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="generate-articles-btn"
          title={!aiQuota.eligible ? 'TurboCharge-only feature' : aiQuota.remaining <= 0 ? `Monthly AI pull quota used (${aiQuota.used}/${aiQuota.limit})` : ''}
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {!aiQuota.eligible ? 'TurboCharge only' : aiQuota.remaining <= 0 ? 'Monthly quota used' : remaining === 0 ? 'Monthly quota used' : `Generate ${remaining} now`}
        </button>
      </div>

      {/* Monthly progress */}
      <div className="bg-gradient-to-r from-sky-50 to-emerald-50 rounded-2xl border border-sky-100 p-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">{data.month_label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {used} / {quota} articles this month
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {aiQuota.eligible
              ? `AI pulls used this month: ${aiQuota.used}/${aiQuota.limit}. Resets on the 1st.`
              : 'AI generation is a TurboCharge-only feature. Resets monthly for Turbo doctors.'}
          </p>
        </div>
        <div className="flex gap-1.5">
          {Array.from({ length: quota }).map((_, i) => (
            <div key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center ${i < used ? 'bg-sky-500 text-white' : 'bg-white border border-slate-200 text-slate-300'}`}>
              {i < used ? <Check size={14} /> : <Sparkles size={14} />}
            </div>
          ))}
        </div>
      </div>

      {msg && (
        <div className="text-sm bg-sky-50 text-sky-700 border border-sky-200 rounded-xl px-4 py-3" data-testid="seo-articles-msg">
          {msg}
        </div>
      )}

      {/* Article list */}
      {data.articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <Newspaper size={32} className="text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>No articles yet</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
            Click <b>Generate now</b> to have the AI write 2 SEO-optimised articles based on your specialty and location.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.articles.map((a) => (
            <ArticleCard key={a.id} article={a} onOpen={() => openArticle(a.id)} onApprove={() => approve(a.id)} onReject={() => reject(a.id)} />
          ))}
        </div>
      )}

      {selected && (
        <ArticleEditor
          article={selected}
          onClose={() => { setSelected(null); setShowHistory(false); }}
          onSaved={async () => { await load(); const r = await api.get(`/dashboard/seo-articles/${selected.id}`); setSelected(r.data); }}
          onApprove={() => approve(selected.id)}
          onReject={() => reject(selected.id)}
          onUnpublish={() => unpublish(selected.id)}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
        />
      )}
    </div>
  );
}

function ArticleCard({ article, onOpen, onApprove, onReject }) {
  const statusStyles = {
    pending_review: 'bg-amber-100 text-amber-700',
    published: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-slate-100 text-slate-500',
  };
  const statusLabel = {
    pending_review: 'Pending review',
    published: 'Published',
    rejected: 'Rejected',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-300 transition-colors" data-testid={`article-card-${article.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${statusStyles[article.status] || 'bg-slate-100 text-slate-500'}`}>
              {statusLabel[article.status] || article.status}
            </span>
            <span className="text-[11px] text-slate-400">{article.primary_specialty}</span>
            {article.edit_count > 0 && (
              <span className="text-[11px] text-slate-500 flex items-center gap-1">
                <History size={11} /> {article.edit_count} edit{article.edit_count === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <h3 className="text-base font-medium text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{article.title}</h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{article.meta_description}</p>
          <p className="text-[11px] text-slate-400 mt-2">Generated {new Date(article.generated_at).toLocaleDateString()}</p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button onClick={onOpen} className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" data-testid={`article-open-${article.id}`}>
            <Edit3 size={12} className="inline mr-1" />Review & Edit
          </button>
          {article.status === 'pending_review' && (
            <>
              <button onClick={onApprove} className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid={`article-approve-${article.id}`}>
                <Check size={12} className="inline mr-1" />Approve
              </button>
              <button onClick={onReject} className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg" data-testid={`article-reject-${article.id}`}>
                <X size={12} className="inline mr-1" />Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ArticleEditor({ article, onClose, onSaved, onApprove, onReject, onUnpublish, showHistory, setShowHistory }) {
  const [title, setTitle] = useState(article.title);
  const [metaDescription, setMetaDescription] = useState(article.meta_description || '');
  const [content, setContent] = useState(article.content);
  const [keywords, setKeywords] = useState((article.keywords || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setTitle(article.title);
    setMetaDescription(article.meta_description || '');
    setContent(article.content);
    setKeywords((article.keywords || []).join(', '));
  }, [article.id]);

  const dirty = title !== article.title || metaDescription !== (article.meta_description || '') || content !== article.content || keywords !== (article.keywords || []).join(', ');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const kw = keywords.split(',').map(k => k.trim()).filter(Boolean);
      await api.patch(`/dashboard/seo-articles/${article.id}`, {
        title, meta_description: metaDescription, content, keywords: kw,
      });
      await onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="article-editor-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <p className="text-xs uppercase tracking-wide font-bold text-slate-400">
              {article.status === 'published' ? 'Published' : article.status === 'rejected' ? 'Rejected' : 'Pending review'}
            </p>
            <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Review SEO Article</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-article-editor"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>}

          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">Title</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-sky-500"
              data-testid="article-title-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">
              Meta description <span className="text-slate-400 font-normal normal-case">— 140-160 chars for Google snippet</span>
            </label>
            <textarea
              value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-sky-500"
              data-testid="article-meta-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">{metaDescription.length} chars</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">
              Keywords <span className="text-slate-400 font-normal normal-case">— comma-separated</span>
            </label>
            <input
              value={keywords} onChange={(e) => setKeywords(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-sky-500"
              data-testid="article-keywords-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-1.5 block">
              Content <span className="text-slate-400 font-normal normal-case">— Markdown; shows as formatted article on your site</span>
            </label>
            <textarea
              value={content} onChange={(e) => setContent(e.target.value)} rows={18}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono focus:outline-none focus:border-sky-500"
              data-testid="article-content-input"
            />
          </div>

          {/* Audit trail */}
          <div className="border-t border-slate-200 pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-sky-600"
              data-testid="toggle-article-history"
            >
              <History size={14} />
              Edit history (audit trail) — {(article.edit_history || []).length} entries
              <ChevronDown size={14} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </button>
            {showHistory && (
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="article-history-list">
                {(article.edit_history || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No edits yet. All edits are recorded permanently and cannot be removed.</p>
                ) : (
                  (article.edit_history || []).slice().reverse().map((h) => (
                    <div key={h.entry_id} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-slate-700">{h.edited_by_email || h.edited_by_role}</span>
                        <span className="text-slate-400">{new Date(h.edited_at).toLocaleString()}</span>
                      </div>
                      <p className="text-slate-500">Changed: {(h.changed_fields || []).join(', ')}</p>
                      {h.changed_fields?.includes('title') && (
                        <p className="text-slate-600 mt-1"><b>Previous title:</b> {h.prev_title}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-2 flex-wrap rounded-b-2xl">
          <div className="flex gap-2">
            {article.status === 'published' && (
              <button onClick={onUnpublish} className="px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="article-unpublish-btn">
                Unpublish
              </button>
            )}
            {article.status === 'pending_review' && (
              <button onClick={onReject} className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg" data-testid="article-editor-reject-btn">
                Reject
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={!dirty || saving} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 disabled:opacity-50" data-testid="article-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save edits
            </button>
            {article.status !== 'published' && (
              <button onClick={onApprove} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700" data-testid="article-editor-approve-btn">
                <Check size={14} />Approve & Publish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

